import {Outlet, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {getProfile} from "@/api/apiMethods.ts";
import {AxiosError} from "axios";
import {FullScreenLoader} from "@/components/loader/FullScreenLoader.tsx";
import {Profile} from "@/api/types.ts";
import {Avatar, Breadcrumbs} from "@telegram-apps/telegram-ui";
import {Link} from "@/components/Link/Link.tsx";
import {useTranslation} from "react-i18next";
import {getUserAcronym} from "@/helpers/getUserAcronym.ts";

export const ProfileLayout = () => {
    const [statusCode, setStatusCode] = useState<number | undefined>(undefined);
    const [profile, setProfile] = useState<Profile | undefined>(undefined);
    const {userId} = useParams();
    const {t} = useTranslation();

    if (!userId) {
        setStatusCode(404);
    }

    const refreshProfile = async () => {
        const fetchProfile = async () => {
            return await getProfile(userId ?? '');
        };
        fetchProfile().then((profile) => {
            setStatusCode(200);
            setProfile(profile);
        }).catch((error) => {
            if (error instanceof AxiosError) {
                setStatusCode(error.response?.status || 500);
            } else {
                setStatusCode(500);
            }
        });
    }

    useEffect(() => {
        refreshProfile().then(() => {
        });
    }, [setProfile, userId]);

    const getTextMaxLen = (text: string, maxLen: number) => {
        if (text.length > maxLen) {
            return text.substring(0, maxLen) + "...";
        }
        return text;
    }

    if (!statusCode) {
        return <FullScreenLoader/>;
    }

    if (statusCode >= 500) {
        return <div>Something went wrong</div>;
    }

    if (statusCode === 404) {
        return <div>Profile not found</div>;
    }

    if (!profile) {
        throw Error("Unknown profile layout state")
    }

    return <div className={"mr-3 ml-3 flex flex-col gap-3"}>
        <Breadcrumbs divider={"slash"}>
            <Breadcrumbs.Item><Link to={"/main"}>{t('navigation.main')}</Link></Breadcrumbs.Item>
            <Breadcrumbs.Item>
                <Link className={"flex justify-between gap-2"} to={`/profiles/${profile.user.id}`}>
                    <Avatar src={profile.user.photo?.sizes[0]?.link}
                            acronym={getUserAcronym(profile.user.fullname)}
                            size={20}/>
                    {getTextMaxLen(profile.user.fullname, 12)}
                </Link>
            </Breadcrumbs.Item>
        </Breadcrumbs>
        <Outlet context={{profile, refreshProfile}}/>
    </div>
}
